import { Fragment, type ReactElement, useContext, useState } from "react"

import { Trans, useTranslation } from "react-i18next"

import { Grid } from "@mui/material"
import { NotificationUtils, Snackbar, Typography } from "@synapse-analytics/synapse-ui"
import type { MRT_ColumnDef } from "material-react-table"

import { Dropzone } from "../../../../../components/form/Dropzone"
import { BaseTable } from "../../../../../components/tables/BaseTable"
import { WorkflowSchemaFeatureRequest } from "../../../../../types/generated/api/WorkflowSchemaFeatureRequest"
import {
  convertCsvToJson,
  generateTableColumnsFromData,
  readFileContentAsync,
} from "../../../../../utils/genericHelpers"
import { WorkflowSchemaContext } from "../../../context/Context"
import { getSchemaFileExample } from "./helpers"

import styles from "./SchemaFileUpload.module.scss"

/**
 * SchemaFileUpload component allows users to upload a schema file (CSV format) for workflow configuration.
 *
 * - Accepts only CSV files and previews the first 10 records for user validation.
 * - Validates the file format and feature types against allowed types.
 *
 * @returns {ReactElement} The rendered upload and preview UI for schema files.
 */
export function SchemaFileUpload(): ReactElement {
  // Context hooks
  const { setIsNextStepEnabled, acceptedFile, setAcceptedFile } = useContext(WorkflowSchemaContext)

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "workflowSchemaDialog.schemaFileUpload",
  })

  // State hooks
  const [csvFilePreview, setCsvFilePreview] = useState<Array<Record<string, string | null>> | undefined>(undefined) // CSV preview data
  const [csvFileColumns, setCsvFileColumns] = useState<MRT_ColumnDef[]>([]) // CSV table columns
  const [showSnackBar, setShowSnackBar] = useState<boolean>(true)

  // Constants
  // Example columns for the required file example table
  const columns: MRT_ColumnDef<{
    value: string
    type: string
  }>[] = [
    {
      header: t("example.column1Header"),
      accessorKey: "value",
      Cell: ({ row }) => (
        <Typography variant="label-regular" variantColor={2}>
          {row.original.value}
        </Typography>
      ),
    },
    {
      header: t("example.column2Header"),
      accessorKey: "type",
      Cell: ({ row }) => (
        <Typography variant="label-regular" variantColor={2}>
          {row.original.type}
        </Typography>
      ),
    },
  ]

  /**
   * Checks if the CSV file rows have valid types for each feature.
   * @param rows - Array of row objects from the CSV file
   * @returns The name of the first invalid feature, or null if all are valid
   */
  const checkFileFormat = (rows: Record<string, string | null>[]): string | null => {
    // Allowed types from the API type definition
    const acceptedTypes = Object.values(WorkflowSchemaFeatureRequest.type)

    for (const row of rows) {
      // Assumes the second column is the type
      const typeValue = Object.values(row)[1]

      // If type is missing, return the feature name (first column)
      if (!typeValue) return Object.values(row)[0] ?? ""

      // If type is not in accepted types, return the feature name
      if (!acceptedTypes.includes(typeValue.toUpperCase() as WorkflowSchemaFeatureRequest.type))
        return Object.values(row)[0] ?? ""
    }

    // All types are valid
    return null
  }

  // TODO: Refactor file handling to use a factory/strategy pattern for different file types.
  /**
   * Handles file acceptance and generates preview data.
   * Converts to table format and generates columns
   * @param file - The uploaded file
   */
  const handleFileAcceptance = async (file: File): Promise<void> => {
    if (file.name.endsWith(".csv")) {
      // Read the rows of the CSV file
      const fileRows = await readFileContentAsync(file)

      // keeping only the first 2 columns
      const jsonConvertedRows = convertCsvToJson(fileRows).map((obj) =>
        Object.fromEntries(Object.entries(obj).slice(0, 2)),
      )

      // If no data, reset state and show error
      if (jsonConvertedRows.length === 0) {
        setCsvFileColumns([])
        setCsvFilePreview([])
        setAcceptedFile([])

        return NotificationUtils.toast(t("notifications.emptyFileError"), {
          severity: "error",
        })
      }

      // Validate feature types in the CSV
      const invalidFeature = checkFileFormat(jsonConvertedRows)

      if (typeof invalidFeature === "string") {
        setCsvFileColumns([])
        setCsvFilePreview([])
        setAcceptedFile([])

        return NotificationUtils.toast(t("notifications.invalidType.description", { featureName: invalidFeature }), {
          title: t("notifications.invalidType.title"),
          severity: "error",
        })
      }

      // get the first 10 rows of the CSV file
      const first10Rows = jsonConvertedRows.slice(0, 10)

      // Set preview data for the table
      setCsvFilePreview(first10Rows)

      // Dynamically generate columns from CSV keys
      const columns = generateTableColumnsFromData(first10Rows)

      setCsvFileColumns(columns)
      setAcceptedFile([file])
      setIsNextStepEnabled(true)
    }
  }

  return (
    <Grid container direction={"column"} gap={2}>
      <Dropzone
        uploadProgress={100}
        selectedFiles={acceptedFile}
        setSelectedFiles={(file: File[]) => {
          if (file[0]) {
            handleFileAcceptance(file[0])
            setIsNextStepEnabled(false)
            setShowSnackBar(true)
          } else if (file.length === 0) setAcceptedFile([])
        }}
        bottomText={t("dropzone.bottomText")}
        titleHeader={{
          title: t("dropzone.title"),
        }}
      />

      {/* Preview or example section */}
      <Grid item className={styles.exampleContainer}>
        {csvFilePreview && csvFileColumns.length !== 0 ? (
          <Fragment>
            {/* Preview of uploaded file */}
            <Typography variant="label-bold">{t("preview.title")}</Typography>

            {showSnackBar && (
              <Snackbar
                fullWidth
                removable
                onRemove={() => setShowSnackBar(false)}
                severity={"info"}
                description={t("preview.info")}
              />
            )}

            <BaseTable
              newStyling
              disableToolbar
              disablePagination
              enableOrdering={false}
              data={csvFilePreview}
              columns={csvFileColumns}
              title={t("preview.tableTitle")}
              pageSize={10}
              isLoading={false}
            />
          </Fragment>
        ) : (
          <Fragment>
            {/* Example file format if no file is uploaded */}
            <Typography variant="label-bold">{t("example.title")}</Typography>

            <Typography variant="label-regular" variantColor={2}>
              <Trans>{t("example.description")}</Trans>
            </Typography>

            <BaseTable
              newStyling
              disableToolbar
              disablePagination
              enableOrdering={false}
              data={getSchemaFileExample(t("example.value"))}
              columns={(columns as MRT_ColumnDef[]) ?? []}
              title={t("example.tableTitle")}
              isLoading={false}
            />
          </Fragment>
        )}
      </Grid>
    </Grid>
  )
}
